import { useCallback, useEffect, useRef, useState } from 'react'
import { TRPCClientError } from '@trpc/client'
import { isDeepEqualData } from '@deepguide-ai/shared'
import localforage from '@/lib/localforageClient'

interface LocalDocumentData {
  docId: string
  workspaceId: string
  branchId?: string
  topicId: string
  content: any // Tiptap JSON content
  lastServerContentHash: string | null
  lastServerUpdatedAt: string | null
  isChangedLocally: boolean
}

interface CachedDocument {
  data: LocalDocumentData
  timestamp: number
}

const documentCache = new Map<string, CachedDocument>()
const CACHE_TTL = 60 * 60 * 1000 // 60 minutes
const MAX_CACHE_SIZE = 1000

const getCachedDocument = (key: string): LocalDocumentData | null => {
  const cached = documentCache.get(key)
  if (!cached) return null
  if (Date.now() - cached.timestamp > CACHE_TTL) {
    documentCache.delete(key)
    return null
  }
  return cached.data
}

const setCachedDocument = (key: string, data: LocalDocumentData): void => {
  if (documentCache.size >= MAX_CACHE_SIZE) {
    const oldestKey = documentCache.keys().next().value
    if (oldestKey) {
      documentCache.delete(oldestKey)
    }
  }
  documentCache.set(key, {
    data: { ...data },
    timestamp: Date.now(),
  })
}

const getCacheKey = (storageKey: string, docId: string) => {
  return `${storageKey}-${docId}`
}

export interface UseTiptapPersistenceOptions {
  docId?: string
  topicId?: string
  workspaceId?: string
  branchId?: string
  storageKey?: string
  autoSave?: boolean
  debounceMs?: number
  onServerSave?: (payload: {
    docId: string
    workspaceId: string
    content: any
  }) => Promise<{ contentHash: string; updatedAt: string }>
  onServerLoad?: (docId: string) => Promise<{
    content: any
    contentHash: string
    updatedAt: string
    topicId: string
  } | null>
}

export function useTiptapPersistence(options: UseTiptapPersistenceOptions) {
  const {
    docId,
    topicId,
    workspaceId,
    branchId,
    storageKey = 'doc',
    debounceMs = 1000 * 60, // save to server every 60 seconds
    onServerSave,
    onServerLoad,
  } = options
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [lastServerContentHash, setLastServerContentHash] = useState<
    string | null
  >(null)
  const [isConflict, setIsConflict] = useState(false)
  const [error, setError] = useState<any>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const lastSavedContentRef = useRef<any>(null)
  const debounceTimeout = useRef<any>(null)
  const contentLoadedSuccessfully = useRef(false)

  // Save to server
  const saveToServer = useCallback(
    async (docId: string, workspaceId: string, newContent: any) => {
      if (!onServerSave || !docId || !workspaceId) {
        return
      }
      // if (currentDocId !== docId) return
      setIsSaving(true)
      try {
        const res = await onServerSave({
          docId,
          workspaceId,
          content: newContent,
        })

        setLastServerContentHash(res.contentHash)
        setLastSavedAt(res.updatedAt)
        setHasUnsavedChanges(false)
        setIsConflict(false)
        lastSavedContentRef.current = newContent

        // Dispatch event to notify that document was saved
        window.dispatchEvent(new CustomEvent('tiptap:document:saved', {
          detail: { docId, workspaceId, contentHash: res.contentHash }
        }))
      } catch (err) {
        setError(err)
        if (err instanceof TRPCClientError && err.data?.code === 'CONFLICT') {
          setIsConflict(true)
        }
      } finally {
        setIsSaving(false)
      }
    },
    [onServerSave]
  )

  // Debounced save
  const debouncedSave = useCallback(
    (docId: string, workspaceId: string, newContent: any) => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
      debounceTimeout.current = setTimeout(() => {
        saveToServer(docId, workspaceId, newContent)
      }, debounceMs)
    },
    [saveToServer, debounceMs]
  )

  // Helper: determine if content is effectively blank
  const isBlankDocument = useCallback((content: any): boolean => {
    if (content == null) return true
    // Plate/Tiptap content might be an array or a { type: 'doc', content: [] } object
    if (Array.isArray(content)) return content.length === 0
    if (typeof content === 'object') {
      // For TipTap doc format {type: 'doc', content: [...]}
      // If content.content is missing, undefined, or empty array, it's blank
      if (content.type === 'doc') {
        if (!Array.isArray(content.content) || content.content.length === 0) {
          return true
        }
        return false
      }
      // For other objects with content array
      if (Array.isArray(content.content)) {
        return content.content.length === 0
      }
    }
    return false
  }, [])

  // Save to localForage and memory cache
  const saveToLocal = useCallback(
    async (content: any, forceSave = false) => {
      if (!docId || !workspaceId || !topicId) {
        console.log(
          '>>> Saving document to local skipped, missing docId, workspaceId, or topicId',
          docId,
          workspaceId,
          topicId
        )
        return
      }

      // Prevent saving blank content if we haven't successfully loaded content yet
      if (!contentLoadedSuccessfully.current && isBlankDocument(content) && !forceSave) {
        console.warn('>>> Refusing to save blank document before initial load completes')
        return
      }

      if (!forceSave && isDeepEqualData(lastSavedContentRef.current, content)) {
        console.log('>>> Auto-save skipped, content is the same')
        return
      }

      // Mark as changed since content is different from last saved
      setHasUnsavedChanges(true)

      const localData: LocalDocumentData = {
        docId,
        workspaceId,
        branchId,
        topicId,
        content,
        lastServerContentHash,
        lastServerUpdatedAt: lastSavedAt || new Date().toISOString(),
        isChangedLocally: true, // Always true when saving new content
      }
      const cacheKey = getCacheKey(storageKey, docId)
      console.log('>>> Saving document to local', cacheKey)
      setCachedDocument(cacheKey, localData)
      await localforage.setItem(cacheKey, localData)
      lastSavedContentRef.current = content
      // save to server
      if (forceSave) {
        saveToServer(docId, workspaceId, content)
      } else {
        debouncedSave(docId, workspaceId, content)
      }
    },
    [
      docId,
      workspaceId,
      branchId,
      topicId,
      lastServerContentHash,
      lastSavedAt,
      hasUnsavedChanges,
      storageKey,
      debouncedSave,
      saveToServer,
      lastSavedContentRef,
      isBlankDocument,
    ]
  )

  // Load from localForage/memory/server
  const loadDocument = useCallback(
    async (targetDocId: string, forceFromServer = false) => {
      setIsLoading(true)
      setError(null)
      contentLoadedSuccessfully.current = false // Reset on new load
      const cacheKey = getCacheKey(storageKey, targetDocId)
      const cacheData = getCachedDocument(cacheKey)

      // Skip cache if forceFromServer is true
      // 当forceFromServer为false的时候，需要判断cacheData是否存在
      if (!forceFromServer && cacheData) {
        let localData = cacheData
        if (localData && localData.docId === targetDocId && !isBlankDocument(localData.content)) {
          setLastServerContentHash(localData.lastServerContentHash)
          setLastSavedAt(localData.lastServerUpdatedAt)
          setHasUnsavedChanges(localData.isChangedLocally)
          lastSavedContentRef.current = localData.content
          contentLoadedSuccessfully.current = true
          setIsLoading(false)
          console.log('>>> Loaded document from local cache', localData.content)
          return localData.content
        }
        localData = (await localforage.getItem(
          cacheKey
        )) as LocalDocumentData | null
        if (localData && localData.docId === targetDocId && !isBlankDocument(localData.content)) {
          setCachedDocument(cacheKey, localData)
          setLastServerContentHash(localData.lastServerContentHash)
          setLastSavedAt(localData.lastServerUpdatedAt)
          setHasUnsavedChanges(localData.isChangedLocally)
          lastSavedContentRef.current = localData.content
          contentLoadedSuccessfully.current = true
          setIsLoading(false)
          console.log('>>> Loaded document from localForage', localData.content)

          // If local data has unsaved changes, prefer it over server data
          if (localData.isChangedLocally) {
            console.log('>>> Local data has unsaved changes, using local version')
            return localData.content
          }
        }
      }

      if (onServerLoad) {
        // Check localForage one more time before loading from server
        // to see if there are unsaved local changes
        const localDataBeforeServer = (await localforage.getItem(cacheKey)) as LocalDocumentData | null

        const serverData = await onServerLoad(targetDocId)
        if (serverData) {
          // If local data has unsaved changes, prefer local over server
          if (localDataBeforeServer?.isChangedLocally && !isBlankDocument(localDataBeforeServer.content)) {
            setCachedDocument(cacheKey, localDataBeforeServer)
            setLastServerContentHash(localDataBeforeServer.lastServerContentHash)
            setLastSavedAt(localDataBeforeServer.lastServerUpdatedAt)
            setHasUnsavedChanges(true)
            lastSavedContentRef.current = localDataBeforeServer.content
            contentLoadedSuccessfully.current = true
            setIsLoading(false)
            return localDataBeforeServer.content
          }

          // No local changes, use server data
          const docData: LocalDocumentData = {
            docId: targetDocId,
            workspaceId: workspaceId!,
            branchId,
            topicId: serverData.topicId,
            content: serverData.content,
            lastServerContentHash: serverData.contentHash,
            lastServerUpdatedAt: serverData.updatedAt,
            isChangedLocally: false,
          }
          setCachedDocument(cacheKey, docData)
          await localforage.setItem(cacheKey, docData)
          setLastServerContentHash(serverData.contentHash)
          setLastSavedAt(serverData.updatedAt)
          setHasUnsavedChanges(false)
          lastSavedContentRef.current = serverData.content
          contentLoadedSuccessfully.current = true
          setIsLoading(false)
          console.log('>>> Loaded document from server', serverData.content)
          return serverData.content
        }
      }
      setLastServerContentHash(null)
      setLastSavedAt(new Date().toISOString())
      setHasUnsavedChanges(false)
      lastSavedContentRef.current = { type: 'doc', content: [] }
      contentLoadedSuccessfully.current = true // Even for empty doc, we've completed loading
      setIsLoading(false)
      return null
    },
    [storageKey, onServerLoad, workspaceId, branchId, isBlankDocument]
  )

  // Force load from server
  const forceLoadFromServer = useCallback(async () => {
    if (!docId) {
      console.warn('>>> Cannot force load from server without docId')
      return null
    }
    return loadDocument(docId, true)
  }, [docId, loadDocument])

  // Auto-save on content change
  // useEffect(() => {
  //   if (!autoSave) return
  //   if (!docId || !workspaceId) return
  //   if (isLoading) return
  //   if (isDeepEqualData(lastSavedContentRef.current, content)) {
  //     console.log('>>> Auto-save skipped, content is the same')
  //     return
  //   }
  //   setHasUnsavedChanges(true)
  //   saveToLocal(content)
  // }, [content, autoSave, docId, workspaceId, isLoading, saveToLocal])

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current)
        debounceTimeout.current = null
      }
    }
  }, [])

  // Reset content loaded flag when docId changes
  useEffect(() => {
    contentLoadedSuccessfully.current = false
  }, [docId])

  return {
    isLoading,
    isSaving,
    lastSavedAt,
    lastServerContentHash,
    isConflict,
    error,
    hasUnsavedChanges,
    saveDocument: saveToLocal,
    loadDocument,
    forceLoadFromServer,
  }
}
